/**
 * The ⌘K verb rows, in the order the optimal design fixes: the current selection's verbs, then
 * every open ask's verbs, then Stop. Each row keeps its keycap and `viko` command so the palette
 * teaches the keyboard and the CLI as it is used. Built from the same `Action[]` as the footer.
 */
import type { Action } from './actions.js'

export interface VerbRow {
  id: string
  group: string
  /** Who the verb acts on: `sel`, an ask's key, or `stop`. */
  owner: string
  /** A verb phrase: an answer reads "Pick 30 seconds", not just the option. */
  label: string
  key: string
  command: string | null
  danger?: boolean
  disabledReason?: string
  run: () => void
}

export interface VerbInput {
  selection?: { title: string; actions: readonly Action[]; key?: string } | undefined
  asks: readonly { key: string; title: string; actions: readonly Action[] }[]
  stops: readonly { title: string; run: () => void }[]
}

/** What an ask offers from ⌘K without opening it: its answers and "decide for me". */
const ASK_VERBS = (a: Action) => !!a.option || a.id === 'delegate'

/**
 * "Accept the suggested answer" is "Pick ⟨suggested⟩", which the palette already lists with a
 * "suggested" qualifier; `s` stays a key alias (app-clarity README §2).
 */
const PALETTE_DUPES = new Set(['suggested'])

const row = (group: string, owner: string, a: Action): VerbRow => ({
  id: `${owner}:${a.id}`,
  group,
  owner,
  label: a.option ? `Pick ${a.label}` : a.label,
  key: a.key,
  command: a.vikoCommand,
  ...(a.danger ? { danger: true } : {}),
  ...(a.disabledReason ? { disabledReason: a.disabledReason } : {}),
  run: a.run,
})

export function verbRows(i: VerbInput): VerbRow[] {
  const out: VerbRow[] = []
  if (i.selection?.actions.length)
    for (const a of i.selection.actions.filter((x) => !PALETTE_DUPES.has(x.id)))
      out.push(row(`Selected: ${i.selection.title}`, 'sel', a))
  for (const ask of i.asks) {
    if (i.selection?.key && i.selection.key === ask.key) continue
    for (const a of ask.actions.filter(ASK_VERBS))
      out.push(row(`Needs you: ${ask.title}`, ask.key, a))
  }
  for (const s of i.stops)
    out.push({
      id: `stop:${s.title}`,
      group: 'Stop',
      owner: 'stop',
      label: `Stop ${s.title}`,
      key: '⇧S',
      command: null,
      danger: true,
      run: s.run,
    })
  return out
}

const UUID = /\b([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi

/**
 * The palette footer shows the highlighted entry's `viko` command with short ids (the first 8
 * characters of a UUID, like a short SHA); ⌘C still copies the full command.
 */
export function shortCommand(command: string): string {
  return command.replace(UUID, '$1')
}
